import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useBusiness } from '../contexts/BusinessContext';
import { useSimilarTasks } from '../hooks/useSimilarTasks';
import { SimilarTaskHint } from './tasks/SimilarTaskHint';
import api from '../api';
import {
  Plus, X, Send, Brain,
  CheckCircle2, BookOpen, Phone, FileText, Layers, Zap,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import ThoughtCard from './parkinglot/ThoughtCard';
import { logger } from '../utils/logger';

const CONVERT_TARGETS = [
  { key: 'task', label: 'Task', icon: CheckCircle2, color: 'var(--pd-focus)' },
  { key: 'reading', label: 'Reading', icon: BookOpen, color: 'var(--pd-warning)' },
  { key: 'call', label: 'Call', icon: Phone, color: 'var(--pd-green)' },
  { key: 'note', label: 'Note', icon: FileText, color: 'var(--pd-muted)' },
];

const PRIORITY_COLORS = {
  URGENT: 'var(--pd-crimson)', HIGH: 'var(--pd-warning)',
  MEDIUM: 'var(--pd-focus)', LOW: 'var(--pd-subtle)',
};

function ClassificationBadge({ classification }) {
  if (!classification || !classification.type) return null;
  const target = CONVERT_TARGETS.find(t => t.key === classification.type);
  if (!target) return null;
  const Icon = target.icon;
  const fields = classification.fields || {};

  return (
    <div data-testid="ai-classification-badge" className="flex items-center gap-1.5 mt-1.5">
      <Sparkles className="w-2.5 h-2.5" style={{ color: target.color }} />
      <span className="text-[9px] font-semibold" style={{ color: target.color }}>
        <Icon className="w-2.5 h-2.5 inline mr-0.5" />{target.label}
      </span>
      {fields.contactName ? (
        <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--pd-panel)', color: 'var(--pd-text)' }}>
          {fields.contactName}
        </span>
      ) : null}
      {fields.priority && fields.priority !== 'MEDIUM' ? (
        <span className="text-[9px] font-bold" style={{ color: PRIORITY_COLORS[fields.priority] || 'var(--pd-muted)' }}>
          {fields.priority}
        </span>
      ) : null}
      {classification.reason ? (
        <span className="text-[9px]" style={{ color: 'var(--pd-subtle)' }}>· {classification.reason}</span>
      ) : null}
    </div>
  );
}

export default function QuickCapture() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('capture');
  const [text, setText] = useState('');
  const [thoughts, setThoughts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [converting, setConverting] = useState(null);
  const [classifying, setClassifying] = useState(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);
  const { activeBusiness, businesses } = useBusiness();
  const location = useLocation();
  const similarMatches = useSimilarTasks(
    text,
    activeBusiness?.id,
    open && mode === 'capture',
    { includeInbox: true },
  );

  // Cache the unclassified set — the polling effect previously called
  // `thoughts.filter(...)` on every render which created a fresh array
  // reference each time and triggered redundant interval setups.
  const hasUnclassified = useMemo(
    () => thoughts.some((t) => !t.classification),
    [thoughts],
  );

  const fetchThoughts = useCallback(async (signal) => {
    try {
      const res = await api.get('/parking-lot', { signal });
      setThoughts(res.data.filter(t => t.status === 'unprocessed'));
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      logger.error('Parking lot fetch failed:', err);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchThoughts();
      if (mode === 'capture' && inputRef.current) inputRef.current.focus();
    }
  }, [open, mode, fetchThoughts]);

  // Poll for AI classification results when in review mode
  useEffect(() => {
    if (open && mode === 'review' && hasUnclassified) {
      pollRef.current = setInterval(fetchThoughts, 2000);
      return () => clearInterval(pollRef.current);
    }
    if (pollRef.current) clearInterval(pollRef.current);
    return undefined;
  }, [open, mode, hasUnclassified, fetchThoughts]);

  useEffect(() => {
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === ' ') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open]);

  const handleCapture = async () => {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    try {
      const bizId = activeBusiness?.id || businesses[0]?.id || null;
      await api.post('/inbox', { title: text.trim(), businessId: bizId, sourceType: 'MANUAL' });
      toast.success('Captured to Inbox');
      setText('');
      try { window.dispatchEvent(new Event('polkdesq:data-mutated')); } catch { /* ignore */ }
    } catch {
      toast.error('Could not capture');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConvert = useCallback(async (thought, targetOverride) => {
    const target = targetOverride || thought.classification?.type || 'task';
    const ai = thought.classification?.fields || {};
    setConverting(thought.id);
    try {
      await api.post(`/parking-lot/${thought.id}/convert`, {
        target,
        title: ai.title || thought.text,
        contactName: ai.contactName || '',
        purpose: ai.purpose || '',
        url: ai.url || '',
        priority: ai.priority || 'MEDIUM',
        estimatedMinutes: ai.estimatedMinutes || 15,
        context: ai.context || '',
      });
      toast.success(`Converted to ${target}${ai.contactName ? ` — ${ai.contactName}` : ''}`);
      fetchThoughts();
    } catch {
      toast.error('Conversion failed');
    } finally {
      setConverting(null);
    }
  }, [fetchThoughts]);

  const handleReclassify = useCallback(async (thoughtId) => {
    setClassifying(thoughtId);
    try {
      const res = await api.post(`/parking-lot/${thoughtId}/classify`);
      if (res.data.classified) {
        toast.success('Reclassified');
        fetchThoughts();
      } else {
        toast.error('Classification failed — try again');
      }
    } catch {
      toast.error('Classification failed');
    } finally {
      setClassifying(null);
    }
  }, [fetchThoughts]);

  const handleDismiss = useCallback(async (thoughtId) => {
    try {
      await api.delete(`/parking-lot/${thoughtId}`);
      setThoughts((prev) => prev.filter((t) => t.id !== thoughtId));
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      logger.error('Parking lot dismiss failed:', err);
    }
  }, []);

  if (location.pathname === '/login' || location.pathname === '/onboarding') return null;

  const unprocessedCount = thoughts.length;

  return (
    <>
      {open ? <div className="fixed inset-0 z-[400] bg-black/10 backdrop-blur-[2px]" onClick={() => setOpen(false)} /> : null}

      {open ? (
        <div
          data-testid="quick-capture-modal"
          className="fixed bottom-24 right-8 z-[401] w-[400px] rounded-[24px] animate-fade-in overflow-hidden"
          style={{ background: 'var(--pd-panel-solid)', border: '1px solid var(--pd-border)', boxShadow: 'var(--pd-shadow-3)' }}
        >
          {/* Header */}
          <div className="px-5 pt-4 pb-2">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4" style={{ color: 'var(--pd-focus)' }} />
                <span className="text-[13px] font-semibold" style={{ color: 'var(--pd-text)' }}>Quick Capture</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(37,99,235,0.08)', color: 'var(--pd-focus)' }}>→ INBOX</span>
              </div>
              <button onClick={() => setOpen(false)} className="pd-icon-button" style={{ width: 28, height: 28 }}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Review tab only surfaces while LEGACY parked thoughts remain
                unprocessed — new captures go straight to the Inbox. */}
            {unprocessedCount > 0 ? (
            <div className="flex gap-1.5">
              {[
                { key: 'capture', label: 'Capture', icon: Zap },
                { key: 'review', label: `Parked (${unprocessedCount})`, icon: Layers },
              ].map(tab => (
                <button
                  key={tab.key}
                  data-testid={`parking-lot-tab-${tab.key}`}
                  onClick={() => setMode(tab.key)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-semibold transition-colors"
                  style={{
                    background: mode === tab.key ? 'var(--pd-text)' : 'var(--pd-panel-2)',
                    color: mode === tab.key ? 'white' : 'var(--pd-muted)',
                  }}
                >
                  <tab.icon className="w-3 h-3" />
                  {tab.label}
                </button>
              ))}
            </div>
            ) : null}
          </div>

          {(mode === 'capture' || unprocessedCount === 0) ? (
            <div className="px-5 py-3">
              <div className="flex items-center gap-2 rounded-2xl px-4 py-3" style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)' }}>
                <Brain className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--pd-subtle)' }} />
                <input
                  ref={inputRef}
                  data-testid="quick-capture-input"
                  type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCapture(); }}
                  placeholder="Capture a thought — lands in your Inbox..."
                  className="flex-1 bg-transparent text-[13px] outline-none placeholder:text-[var(--pd-subtle)]"
                  style={{ color: 'var(--pd-text)' }}
                />
                <button
                  data-testid="quick-capture-submit"
                  onClick={handleCapture}
                  disabled={!text.trim() || submitting}
                  className="w-8 h-8 rounded-xl flex items-center justify-center transition-all"
                  style={{
                    background: text.trim() ? 'var(--pd-text)' : 'var(--pd-panel)',
                    color: text.trim() ? 'white' : 'var(--pd-subtle)',
                    opacity: submitting ? 0.6 : 1,
                  }}
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="text-[10px] mt-2 flex items-center justify-between" style={{ color: 'var(--pd-subtle)' }}>
                <span>{activeBusiness ? `Into ${activeBusiness.shortCode}` : 'Default business'}</span>
                <span>Enter to capture · sort it from Inbox</span>
              </div>
              {similarMatches.length > 0 && (
                <div className="mt-2">
                  <SimilarTaskHint matches={similarMatches} compact />
                </div>
              )}
            </div>
          ) : (
            <div className="px-4 py-3 max-h-[360px] overflow-y-auto" data-testid="parking-lot-review-tray">
              {thoughts.length === 0 ? (
                <div className="text-center py-8">
                  <Brain className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--pd-subtle)' }} />
                  <div className="text-[12px] font-medium" style={{ color: 'var(--pd-muted)' }}>All clear</div>
                  <div className="text-[10px] mt-1" style={{ color: 'var(--pd-subtle)' }}>No unprocessed thoughts</div>
                </div>
              ) : (
                <div className="space-y-2">
                  {thoughts.map((thought) => (
                    <ThoughtCard
                      key={thought.id}
                      thought={thought}
                      classification={thought.classification}
                      convertTargets={CONVERT_TARGETS}
                      converting={converting}
                      classifying={classifying}
                      onConvert={handleConvert}
                      onReclassify={handleReclassify}
                      onDismiss={handleDismiss}
                      renderClassificationBadge={(ai) => <ClassificationBadge classification={ai} />}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}

      <button
        data-testid="quick-capture-fab"
        onClick={() => setOpen(prev => !prev)}
        className="fixed bottom-8 right-8 z-[399] w-14 h-14 rounded-2xl flex items-center justify-center text-white transition-all duration-300"
        style={{
          background: open ? 'var(--pd-muted)' : 'var(--pd-text)',
          boxShadow: '0 8px 32px rgba(23,26,34,0.25), 0 2px 8px rgba(23,26,34,0.15)',
          transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
        }}
        title="Quick Capture (Ctrl+Shift+Space)"
      >
        <Plus className="w-6 h-6" />
        {!open && unprocessedCount > 0 ? (
          <span data-testid="parking-lot-badge" className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ background: 'var(--pd-crimson)' }}>
            {unprocessedCount > 9 ? '9+' : unprocessedCount}
          </span>
        ) : null}
      </button>
    </>
  );
}
